import Link from '@components/Common/Link';
import NavItem from '@components/Common/NavItem';
import NavLink from '@components/Common/NavLink';
import {CMS_BASE_URL, REFERENCE_BASE_URL} from '@constants/common';
import {
  AskOfferLinks,
  BuyersGuidsLink,
  ContactInformationLinks,
  DownloadLoadBrochuresLink,
  ProductsLink,
} from '@constants/navigations';
import {LocaleEnum} from '@enums/locale';
import {
  faAddressCard,
  faAngleRight,
  faBookOpen,
  faCommentDots,
  faDownload,
  faStore,
} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ComponentWithChildrenProps} from '@interfaces/component';
import {useTranslation} from 'next-i18next';
import {useRouter} from 'next/router';
import {useEffect, useMemo, useState} from 'react';
import styles from './style.module.css';

export interface CMSMenu extends Record<string, any> {
  sub_breadcrumb?: CMSMenu[];
  link_name?: string;
}

interface CMSSideMenuProps extends ComponentWithChildrenProps {
  parentContentId?: number;
  contentId: number;
  showMobileNav: boolean;

  menu: CMSMenu[];
  isReferencePage?: boolean;
}

const countSubItems = (menuItem: CMSMenu): number => {
  if (!menuItem.sub_breadcrumb) return 0;
  return menuItem.sub_breadcrumb
    .filter(menu => !!menu.link_name)
    .reduce((count, subItem) => {
      return count + 1 + countSubItems(subItem);
    }, 0);
};

const getActivePath = (
  menu: CMSMenu[],
  contentId: number,
  path: Set<number> = new Set()
): Set<number> => {
  for (const item of menu) {
    const id = item.id;
    if (id === contentId) {
      path.add(id);
      return path;
    }
    if (item.sub_breadcrumb) {
      const subPath = getActivePath(
        item.sub_breadcrumb ?? [],
        contentId,
        new Set(path)
      );
      if (subPath.has(contentId)) {
        subPath.add(id);
        return subPath;
      }
    }
  }
  return path;
};

export default function CMSSideMenu(props: CMSSideMenuProps) {
  const {t} = useTranslation('common');
  const {parentContentId, contentId, menu, showMobileNav, isReferencePage} =
    props;
  const router = useRouter();
  const locale = router.locale as unknown as LocaleEnum;
  const baseURL = isReferencePage
    ? REFERENCE_BASE_URL[locale].link
    : CMS_BASE_URL[locale].link;
  const slashContentId = isReferencePage
    ? `/${router.query.contentContainerId}`
    : '';

  const activePath = useMemo(() => {
    const path = getActivePath(menu ?? [], contentId);
    if (parentContentId) {
      path.add(parentContentId);
    }
    return path;
  }, [contentId, menu, parentContentId]);

  const [expandedItems, setExpandedItems] = useState<Set<number>>(() =>
    getActivePath(menu ?? [], contentId)
  );

  useEffect(() => {
    setExpandedItems(getActivePath(menu, contentId));
  }, [contentId, menu]);

  const toggleExpand = (id: number) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const RenderMenuItem = ({
    item,
    level = 0,
  }: {
    item: CMSMenu;
    level: number;
  }) => {
    const link_name = item['link_name'];
    const slug = item['seo_friendly_url'];
    const id = item.id;
    if (!link_name) return null;

    const subItemsCount = countSubItems(item);
    const isActive = activePath.has(id);
    const hasSubItems =
      item.sub_breadcrumb &&
      item.sub_breadcrumb?.filter(sub_menu => !!sub_menu.link_name).length > 0;
    const isExpanded = expandedItems.has(id);
    const url = `${baseURL}${slug}${slashContentId}`;

    return (
      <NavItem
        key={id}
        classNames={`${isActive ? styles.activeLink : ''} ${
          isExpanded ? styles.expanded : ''
        }`}
        locale={locale}
      >
        <div className={styles.menuItemHeader}>
          {hasSubItems ? (
            <button
              type="button"
              className={styles.expandButton}
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${link_name}`}
              onClick={() => toggleExpand(id)}
            >
              <FontAwesomeIcon
                className={`${styles.caretIcon} ${
                  isExpanded ? styles.caretIconExpanded : ''
                }`}
                icon={faAngleRight}
              />
            </button>
          ) : (
            <span className={styles.caretSpacer} aria-hidden="true" />
          )}
          {level >= 2 ? (
            <>
              <Link href={url} target="_blank" rel="noopener noreferrer">
                <span>{link_name}</span>
              </Link>
            </>
          ) : (
            <>
              <Link href={url}>
                <span>{link_name}</span>
              </Link>
            </>
          )}
          {subItemsCount > 0 && (
            <button
              type="button"
              className={styles.countButton}
              aria-label={`${link_name} has ${subItemsCount} items`}
              onClick={() => toggleExpand(id)}
            >
              <span className={styles.itemCount}>{subItemsCount}</span>
            </button>
          )}
        </div>

        {hasSubItems && isExpanded && (
          <ul className={`${styles.subMenu} ${styles[`level-${level + 1}`]}`}>
            {item.sub_breadcrumb
              ?.filter(sub_menu => sub_menu['link_name'])
              .map(sub_menu => (
                <RenderMenuItem
                  key={sub_menu.id}
                  item={sub_menu}
                  level={level + 1}
                />
              ))}
          </ul>
        )}
      </NavItem>
    );
  };

  return (
    <div
      className={`${styles.accountSideMenuContainer} ${
        showMobileNav ? styles.showMobileNav : ''
      }`}
    >
      <ul className={styles.accountSideMenu}>
        {menu.map(item => (
          <RenderMenuItem
            key={item.id}
            item={item}
            level={0}
          />
        ))}
      </ul>
      <h4 className={`${menu.length > 0 ? 'mt-4' : 'mt-0'} `}>
        {t<string>('footer.quick_links.header')}
      </h4>
      <ul className={`${styles.accountSideMenu} ${styles.quickLinks}`}>
        <NavLink
          href={ContactInformationLinks[`link_${locale}`]}
          locale={locale}
        >
          <FontAwesomeIcon icon={faAddressCard} />
          <span>{ContactInformationLinks[`name_${locale}`]}</span>
        </NavLink>
        <NavLink href={ProductsLink[`${locale}`]} locale={locale}>
          <FontAwesomeIcon icon={faStore} />
          <span>{ProductsLink[`name_${locale}`]}</span>
        </NavLink>
        <NavLink href={DownloadLoadBrochuresLink[`link_${locale}`]}>
          <FontAwesomeIcon icon={faDownload} />
          <span>{DownloadLoadBrochuresLink[`name_${locale}`]}</span>
        </NavLink>
        <NavLink href={BuyersGuidsLink[`link_${locale}`]}>
          <FontAwesomeIcon icon={faBookOpen} />
          <span>{BuyersGuidsLink[`name_${locale}`]}</span>
        </NavLink>
        <NavLink href={AskOfferLinks[`link_${locale}`]}>
          <FontAwesomeIcon icon={faCommentDots} />
          <span>{AskOfferLinks[`name_${locale}`]}</span>
        </NavLink>
      </ul>
    </div>
  );
}
